import React, { useState } from 'react';
import {
  Box,
  Alert,
  Stack,
  MenuItem,
  Grid,
  alpha,
  useTheme,
} from '@mui/material';
import GlowButton from './shared/GlowButton';
import UtilityHeader from './shared/UtilityHeader';
import StyledTextArea from './shared/StyledTextArea';
import StyledSelect from './shared/StyledSelect';

// MD5 implementation (Web Crypto API doesn't support MD5)
const md5 = (input: string): string => {
  const rotateLeft = (x: number, n: number) => (x << n) | (x >>> (32 - n));

  const addUnsigned = (x: number, y: number) => {
    const x8 = x & 0x80000000;
    const y8 = y & 0x80000000;
    const x4 = x & 0x40000000;
    const y4 = y & 0x40000000;
    const result = (x & 0x3fffffff) + (y & 0x3fffffff);
    if (x4 & y4) return result ^ 0x80000000 ^ x8 ^ y8;
    if (x4 | y4) {
      if (result & 0x40000000) return result ^ 0xc0000000 ^ x8 ^ y8;
      return result ^ 0x40000000 ^ x8 ^ y8;
    }
    return result ^ x8 ^ y8;
  };

  const F = (x: number, y: number, z: number) => (x & y) | (~x & z);
  const G = (x: number, y: number, z: number) => (x & z) | (y & ~z);
  const H = (x: number, y: number, z: number) => x ^ y ^ z;
  const I = (x: number, y: number, z: number) => y ^ (x | ~z);

  const convertToWordArray = (str: string) => {
    const utf8 = unescape(encodeURIComponent(str));
    const len = utf8.length;
    const numWords = (((len + 8) >>> 6) + 1) << 4;
    const words: number[] = new Array(numWords).fill(0);
    for (let i = 0; i < len; i++) {
      words[i >>> 2] |= utf8.charCodeAt(i) << ((i % 4) * 8);
    }
    words[len >>> 2] |= 0x80 << ((len % 4) * 8);
    words[numWords - 2] = len * 8;
    return words;
  };

  const wordToHex = (value: number) => {
    let hex = '';
    for (let i = 0; i < 4; i++) {
      hex += ((value >>> (i * 8)) & 0xff).toString(16).padStart(2, '0');
    }
    return hex;
  };

  const x = convertToWordArray(input);
  let [a, b, c, d] = [0x67452301, 0xefcdab89, 0x98badcfe, 0x10325476];

  const S = [7, 12, 17, 22, 5, 9, 14, 20, 4, 11, 16, 23, 6, 10, 15, 21];
  const K = [
    0xd76aa478, 0xe8c7b756, 0x242070db, 0xc1bdceee, 0xf57c0faf, 0x4787c62a, 0xa8304613, 0xfd469501,
    0x698098d8, 0x8b44f7af, 0xffff5bb1, 0x895cd7be, 0x6b901122, 0xfd987193, 0xa679438e, 0x49b40821,
    0xf61e2562, 0xc040b340, 0x265e5a51, 0xe9b6c7aa, 0xd62f105d, 0x02441453, 0xd8a1e681, 0xe7d3fbc8,
    0x21e1cde6, 0xc33707d6, 0xf4d50d87, 0x455a14ed, 0xa9e3e905, 0xfcefa3f8, 0x676f02d9, 0x8d2a4c8a,
    0xfffa3942, 0x8771f681, 0x6d9d6122, 0xfde5380c, 0xa4beea44, 0x4bdecfa9, 0xf6bb4b60, 0xbebfbc70,
    0x289b7ec6, 0xeaa127fa, 0xd4ef3085, 0x04881d05, 0xd9d4d039, 0xe6db99e5, 0x1fa27cf8, 0xc4ac5665,
    0xf4292244, 0x432aff97, 0xab9423a7, 0xfc93a039, 0x655b59c3, 0x8f0ccc92, 0xffeff47d, 0x85845dd1,
    0x6fa87e4f, 0xfe2ce6e0, 0xa3014314, 0x4e0811a1, 0xf7537e82, 0xbd3af235, 0x2ad7d2bb, 0xeb86d391,
  ];

  for (let k = 0; k < x.length; k += 16) {
    const [AA, BB, CC, DD] = [a, b, c, d];
    for (let i = 0; i < 64; i++) {
      let f: number, g: number;
      if (i < 16) {
        [f, g] = [F(b, c, d), i];
      } else if (i < 32) {
        [f, g] = [G(b, c, d), (5 * i + 1) % 16];
      } else if (i < 48) {
        [f, g] = [H(b, c, d), (3 * i + 5) % 16];
      } else {
        [f, g] = [I(b, c, d), (7 * i) % 16];
      }
      const temp = d;
      d = c;
      c = b;
      b = addUnsigned(b, rotateLeft(addUnsigned(addUnsigned(a, f), addUnsigned(x[k + g], K[i])), S[Math.floor(i / 16) * 4 + (i % 4)]));
      a = temp;
    }
    [a, b, c, d] = [addUnsigned(a, AA), addUnsigned(b, BB), addUnsigned(c, CC), addUnsigned(d, DD)];
  }

  return wordToHex(a) + wordToHex(b) + wordToHex(c) + wordToHex(d);
};

const HashUtility: React.FC = () => {
  const theme = useTheme();
  const [input, setInput] = useState(() => localStorage.getItem('hash-input') || '');
  const [error, setError] = useState('');
  const [algorithm, setAlgorithm] = useState(() => localStorage.getItem('hash-algorithm') || 'SHA-256');
  const [output, setOutput] = useState(() => localStorage.getItem('hash-output') || '');

  React.useEffect(() => {
    localStorage.setItem('hash-input', input);
  }, [input]);

  React.useEffect(() => {
    localStorage.setItem('hash-algorithm', algorithm);
  }, [algorithm]);

  React.useEffect(() => {
    localStorage.setItem('hash-output', output);
  }, [output]);

  const generateHash = async () => {
    try {
      setError('');
      setOutput('');
      if (!input.trim()) {
        setError('Please enter text to hash');
        return;
      }
      const encoder = new TextEncoder();
      const data = encoder.encode(input);
      let hashHex: string;
      if (algorithm === 'MD5') {
        hashHex = md5(input);
      } else {
        let hashBuffer: ArrayBuffer;
        switch (algorithm) {
          case 'SHA-1': hashBuffer = await crypto.subtle.digest('SHA-1', data); break;
          case 'SHA-256': hashBuffer = await crypto.subtle.digest('SHA-256', data); break;
          case 'SHA-384': hashBuffer = await crypto.subtle.digest('SHA-384', data); break;
          case 'SHA-512': hashBuffer = await crypto.subtle.digest('SHA-512', data); break;
          default: setError('Unsupported hash algorithm'); return;
        }
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      }
      setOutput(hashHex);
    } catch (err) {
      setError('Error generating hash');
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setError('');
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(output);
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
  };

  return (
    <Box>
      <UtilityHeader 
        title="Hash Generator" 
        description="Generate secure cryptographic hashes with industry-standard algorithms." 
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3, border: '1px solid', borderColor: alpha(theme.palette.error.main, 0.3) }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={3}>
            <StyledTextArea
              label="Input Text"
              rows={10}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Enter text to hash..."
            />

            <StyledSelect 
              label="Algorithm" 
              value={algorithm} 
              onChange={(e) => setAlgorithm(e.target.value as string)}
            >
              <MenuItem value="MD5">MD5</MenuItem>
              <MenuItem value="SHA-1">SHA-1</MenuItem>
              <MenuItem value="SHA-256">SHA-256</MenuItem>
              <MenuItem value="SHA-384">SHA-384</MenuItem>
              <MenuItem value="SHA-512">SHA-512</MenuItem>
            </StyledSelect>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <StyledTextArea
            label="Hash Output"
            accentColor="secondary"
            rows={13.5}
            value={output}
            placeholder="Hash will appear here..."
            InputProps={{
              readOnly: true,
              sx: { 
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.85rem',
                lineHeight: 1.6,
                bgcolor: 'rgba(0,0,0,0.4)',
                borderRadius: 3,
                color: 'primary.main',
                '& fieldset': { borderColor: output ? 'primary.main' : 'rgba(255,255,255,0.1)' },
              }
            }}
          />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={2} sx={{ mt: 4, flexWrap: 'wrap', gap: 2 }}>
        <GlowButton
          size="large"
          onClick={generateHash}
          disabled={!input.trim()}
          sx={{ px: 4 }}
        >
          Generate
        </GlowButton>
        <GlowButton
          variant="outlined"
          disableGlow
          size="large"
          onClick={copyToClipboard}
          disabled={!output}
          sx={{ px: 4 }}
        >
          Copy Hash
        </GlowButton>
        <Box sx={{ flexGrow: 1 }} />
        <GlowButton
          variant="outlined"
          disableGlow
          color="inherit"
          onClick={clearAll}
          disabled={!input.trim() && !output.trim()}
          sx={{ opacity: 0.7, borderColor: 'rgba(255,255,255,0.2)' }}
        >
          Clear
        </GlowButton>
      </Stack>
    </Box>
  );
};

export default HashUtility;
